// Lightweight, provider-agnostic analytics.
//
// Each adapter implements init(), track(event, props) and trackPageview(props).
// The core `analytics` object wires up auto-trackers and exposes manual helpers.
// To switch to a real backend later, implement an adapter below and change the
// single `adapter` assignment — no call sites need to change.

// ---- Adapters ----------------------------------------------------------------

const ConsoleAdapter = {
  init() {
    console.info('[analytics] init (ConsoleAdapter)');
  },
  track(event, props) {
    console.info(`[analytics] ${event}`, props || {});
  },
  trackPageview(props) {
    console.info('[analytics] pageview', props || {});
  },
};

// Self-hosted Umami. The tracking script (loaded in public/index.html) sets up
// window.umami; this adapter just forwards events to it. Optional-chaining guards
// the brief window before the deferred script has loaded.
const UmamiAdapter = {
  init() {},                                            // script self-initializes
  track(event, props) { window.umami?.track(event, props); },
  trackPageview() { window.umami?.track(); },           // no-arg = pageview at current URL
};

// Future backend — uncomment and select below to swap providers.
//
// const PlausibleAdapter = {
//   init() {},
//   track(event, props) { window.plausible?.(event, { props }); },
//   trackPageview(props) { window.plausible?.('pageview', { props }); },
// };

const isLocalhost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

// Console logging in local dev, Umami in production. To disable analytics, remove
// the Umami <script> from public/index.html — track() then no-ops via optional chaining.
const adapter = isLocalhost ? ConsoleAdapter : UmamiAdapter;

// ---- Per-page state ----------------------------------------------------------
// In this SPA each route change is treated as a fresh "page" via trackPageview().

const TIME_THRESHOLDS = [10, 30, 60, 120, 300];
const SCROLL_THRESHOLDS = [25, 50, 75, 100];
const TOOL_SESSION_KEY = 'player-properties-tool-session';

let initialized = false;
let pageStart = Date.now();
let firedTime = new Set();
let firedScroll = new Set();
let heartbeatId = null;
let leaveFired = false;
let rafPending = false;

function currentPage() {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

function emit(event, props) {
  adapter.track(event, { page: currentPage(), ...props });
}

function readToolSession() {
  if (typeof window === 'undefined') {
    return { clickCount: 0, lastTool: null, lastToolName: null };
  }

  try {
    const stored = window.sessionStorage.getItem(TOOL_SESSION_KEY);
    return stored
      ? JSON.parse(stored)
      : { clickCount: 0, lastTool: null, lastToolName: null };
  } catch {
    return { clickCount: 0, lastTool: null, lastToolName: null };
  }
}

function writeToolSession(session) {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(TOOL_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Analytics state is best-effort; blocked storage should not affect clicks.
  }
}

function normalizeTool(tool, props = {}) {
  if (tool && typeof tool === 'object') {
    return {
      id: tool.id,
      name: tool.name || tool.id,
    };
  }

  return {
    id: tool,
    name: props.toolName || tool,
  };
}

// ---- Auto-trackers -----------------------------------------------------------

function checkTimeOnPage() {
  const seconds = (Date.now() - pageStart) / 1000;
  TIME_THRESHOLDS.forEach((t) => {
    if (!firedTime.has(t) && seconds >= t) {
      firedTime.add(t);
      emit('time_on_page', { seconds: t });
    }
  });
}

function startHeartbeat() {
  if (heartbeatId !== null) clearInterval(heartbeatId);
  heartbeatId = setInterval(checkTimeOnPage, 1000);
}

function checkScrollDepth() {
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop || 0;
  const viewport = window.innerHeight || doc.clientHeight || 0;
  const full = doc.scrollHeight || 0;
  if (full <= 0) return;
  const depth = Math.min(100, ((scrollTop + viewport) / full) * 100);
  SCROLL_THRESHOLDS.forEach((t) => {
    if (!firedScroll.has(t) && depth >= t) {
      firedScroll.add(t);
      emit('scroll_depth', { depth: t });
    }
  });
  // Once every threshold has fired there's nothing left to measure — drop the
  // scroll listener so it stops running on every frame for the rest of the page.
  if (firedScroll.size === SCROLL_THRESHOLDS.length) {
    window.removeEventListener('scroll', onScroll);
  }
}

function onScroll() {
  if (rafPending) return;
  rafPending = true;
  requestAnimationFrame(() => {
    rafPending = false;
    checkScrollDepth();
  });
}

function firePageLeave() {
  if (leaveFired) return;
  leaveFired = true;
  emit('page_leave', { seconds: Math.round((Date.now() - pageStart) / 1000) });
}

function onVisibilityChange() {
  if (document.hidden) firePageLeave();
}

function onDocumentClick(e) {
  const a = e.target.closest && e.target.closest('a[href]');
  if (!a) return;

  // Tool cards are tracked manually via trackToolInteraction — skip them here
  // so a tool click doesn't also log a generic outbound_click.
  if (a.closest('[data-tool-id]')) return;

  if (a.hostname && a.hostname !== window.location.hostname) {
    emit('outbound_click', { href: a.href });
    emit('outbound_link_click', { href: a.href, linkType: 'external' });
  }
  if (a.dataset && a.dataset.track) {
    emit('click', { label: a.dataset.track, href: a.href });
  }
}

function resetPage() {
  pageStart = Date.now();
  firedTime = new Set();
  firedScroll = new Set();
  leaveFired = false;
  startHeartbeat();
  // Re-arm scroll tracking for the new page. checkScrollDepth() may have removed
  // the listener after the previous page hit 100%; addEventListener dedupes an
  // identical registration, so re-adding here is safe whether or not it's still
  // attached. Short pages may already satisfy thresholds on load.
  window.addEventListener('scroll', onScroll, { passive: true });
  checkScrollDepth();
}

// ---- Core object -------------------------------------------------------------

const analytics = {
  init() {
    if (initialized || typeof window === 'undefined') return;
    initialized = true;
    adapter.init();

    document.addEventListener('click', onDocumentClick, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', firePageLeave);
  },

  trackPageview(props) {
    resetPage();
    adapter.trackPageview({ page: currentPage(), ...props });
  },

  track(event, props) {
    emit(event, props);
  },

  // ---- Manual helpers ----
  trackSidebar(action, props) {
    emit('sidebar', { action, ...props });
  },

  trackProfileView(playerId, props) {
    emit('profile_view', { playerId, ...props });
  },

  trackToolInteraction(tool, action, props) {
    emit('tool_interaction', { tool, action, ...props });
  },

  trackToolClick(tool, props) {
    const currentTool = normalizeTool(tool, props);
    const session = readToolSession();
    const clickIndex = (Number(session.clickCount) || 0) + 1;
    const eventProps = {
      ...props,
      tool: currentTool.id,
      toolId: currentTool.id,
      toolName: currentTool.name,
      action: 'click',
      clickIndex,
    };

    emit('tool_interaction', eventProps);
    emit('outbound_link_click', {
      ...props,
      tool: currentTool.id,
      toolId: currentTool.id,
      toolName: currentTool.name,
      linkType: 'tool',
      clickIndex,
    });

    if (session.lastTool) {
      emit('tool_navigation', {
        ...props,
        fromTool: session.lastTool,
        fromToolName: session.lastToolName || session.lastTool,
        toTool: currentTool.id,
        toToolName: currentTool.name,
        path: `${session.lastTool} -> ${currentTool.id}`,
        clickIndex,
      });
    }

    writeToolSession({
      clickCount: clickIndex,
      lastTool: currentTool.id,
      lastToolName: currentTool.name,
    });
  },

  trackFavoriteAdded(tool, props) {
    const currentTool = normalizeTool(tool, props);
    emit('favorite_added', {
      ...props,
      tool: currentTool.id,
      toolId: currentTool.id,
      toolName: currentTool.name,
    });
  },

  trackFavoriteRemoved(tool, props) {
    const currentTool = normalizeTool(tool, props);
    emit('favorite_removed', {
      ...props,
      tool: currentTool.id,
      toolId: currentTool.id,
      toolName: currentTool.name,
    });
  },
};

export default analytics;
